import type { DeployRecord } from "@botallion/contracts";
import { nameOf } from "../describe.ts";
import { html, link, type Html } from "../html.ts";
import type { StackView } from "../stack.ts";
import { chip, clock, empty, shortSha, type Tone, type ViewContext } from "./common.ts";

const STATUS_TONE: Record<DeployRecord["status"], Tone> = { queued: "muted", deploying: "warn", succeeded: "good", failed: "block", torn_down: "muted" };

function deployRow(ctx: ViewContext, d: DeployRecord): Html {
  const { state } = ctx;
  return html`<tr>
    <td><strong>${d.branch}</strong><div class="muted">${d.stack}</div></td>
    <td>${chip(d.status.replace("_", " "), STATUS_TONE[d.status])}</td>
    <td><code>${shortSha(d.commit)}</code></td>
    <td>${d.urls.length ? html`<ul class="urls">${d.urls.map((u) => html`<li>${u.worker}: ${link(u.url)}</li>`)}</ul>` : html`<span class="muted">none</span>`}</td>
    <td>${d.migrationsApplied.map((m) => html`<div>${m.database}: ${m.migrations.join(", ") || "none"}</div>`)}</td>
    <td>${d.kind === "preview"
      ? d.approvedBy
        ? chip(`promotion approved by ${nameOf(state, d.approvedBy)} at ${clock(d.approvedAt)}`, "good")
        : d.status === "succeeded"
          ? chip(ctx.approver.canApprove ? "waiting for your approval" : "waiting for approval", "human")
          : html`<span class="muted">not ready</span>`
      : html`<span class="muted">finished ${clock(d.finishedAt)}</span>`}</td>
  </tr>`;
}

/** Secret names and where each one lives. Values never exist on this side: see stack.ts. */
export function stackPanel(stack: StackView): Html {
  return html`<div class="stack">
    <h3>Stack <code>${stack.name}</code> <span class="muted">deploy order: ${stack.deployOrder.join(" → ")}; previews ${stack.previewNaming}, production ${stack.productionNaming}</span></h3>
    <table class="workers"><thead><tr><th>Worker</th><th>Bindings</th><th>Secrets (name and source only)</th><th>Vars (names only)</th></tr></thead>
    <tbody>${stack.workers.map((w) => html`<tr>
      <td><strong>${w.name}</strong><div class="muted"><code>${w.entry}</code></div>${w.routes.length ? html`<div class="muted">${w.routes.join(", ")}</div>` : ""}${w.crons.length ? html`<div class="muted">cron ${w.crons.join(", ")}</div>` : ""}</td>
      <td>${w.bindings.length ? w.bindings.map((b) => html`<div><code>${b.name}</code> <span class="muted">${b.kind} ${b.target}</span></div>`) : html`<span class="muted">none</span>`}</td>
      <td>${w.secrets.length
        ? w.secrets.map((s) => html`<div class="secret"><code>${s.name}</code> <span class="muted">from ${s.source.type} store <code>${s.source.storeId}</code>, production <code>${s.source.secretName}</code>, previews <code>${s.source.previewSecretName}</code></span></div>`)
        : html`<span class="muted">none</span>`}</td>
      <td>${w.varNames.length ? w.varNames.map((n) => html`<code>${n}</code> `) : html`<span class="muted">none</span>`}</td>
    </tr>`)}</tbody></table>
  </div>`;
}

/** View 6: preview links per branch, production status, and the stack's secrets by name and source. */
export function deploysView(ctx: ViewContext): Html {
  const { state } = ctx;
  const all = Object.values(state.deploys).sort((a, b) => b.startedAt.localeCompare(a.startedAt));
  const production = all.filter((d) => d.kind === "production");
  const latestProd = production[0];
  // One row per branch: its latest preview.
  const previews = new Map<string, DeployRecord>();
  for (const d of all) if (d.kind === "preview" && !previews.has(`${d.stack}/${d.branch}`)) previews.set(`${d.stack}/${d.branch}`, d);
  const head = html`<thead><tr><th>Branch</th><th>Status</th><th>Commit</th><th>URLs</th><th>Migrations</th><th>Promotion</th></tr></thead>`;
  return html`<div class="prod">
      <strong>Production:</strong>
      ${latestProd
        ? html`${chip(latestProd.status.replace("_", " "), STATUS_TONE[latestProd.status])} <code>${shortSha(latestProd.commit)}</code> ${latestProd.urls.map((u) => html`${link(u.url)} `)}<span class="muted">${clock(latestProd.startedAt)}</span>`
        : html`<span class="muted">nothing deployed to production yet. Approve a preview to promote it</span>`}
    </div>
    <h3>Previews</h3>
    ${previews.size ? html`<table class="deploys">${head}<tbody>${[...previews.values()].map((d) => deployRow(ctx, d))}</tbody></table>` : empty("No previews yet. A preview deploys when an agent lands work on a branch.")}
    ${ctx.stack ? stackPanel(ctx.stack) : ""}`;
}
